import { useState, useEffect } from 'react';
import { Container, Card, Form, Button, Row, Col, Alert, Spinner } from 'react-bootstrap';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../firebase/firebase';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';

const Availability = () => {
  const navigate = useNavigate();
  const { currentUser, userDetails } = useAuth();  // Add currentUser here
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const initialAvailability = {
    monday: { isAvailable: false, startTime: '09:00', endTime: '17:00' },
    tuesday: { isAvailable: false, startTime: '09:00', endTime: '17:00' },
    wednesday: { isAvailable: false, startTime: '09:00', endTime: '17:00' },
    thursday: { isAvailable: false, startTime: '09:00', endTime: '17:00' },
    friday: { isAvailable: false, startTime: '09:00', endTime: '17:00' },
    saturday: { isAvailable: false, startTime: '09:00', endTime: '17:00' },
    sunday: { isAvailable: false, startTime: '09:00', endTime: '17:00' }
  };

  const [availability, setAvailability] = useState(initialAvailability);

  // Check authentication and fetch data
  useEffect(() => {
    const checkAuthAndFetchData = async () => {
      try {
        if (!currentUser) {
          console.log('No current user');
          navigate('/login');
          return;
        }

        if (!userDetails) {
          console.log('No user details');
          setError('User details not loaded. Please try refreshing the page.');
          return;
        }

        console.log('User authenticated:', { currentUser: currentUser.uid, userDetails });

        // Fetch existing availability
        const availabilityRef = doc(db, 'availability', currentUser.uid);
        const availabilityDoc = await getDoc(availabilityRef);
        
        if (availabilityDoc.exists()) {
          setAvailability(availabilityDoc.data().availability);
        }
      } catch (error) {
        console.error('Error in initialization:', error);
        setError('Error loading availability data');
      } finally {
        setLoading(false);
      }
    };

    checkAuthAndFetchData();
  }, [currentUser, userDetails, navigate]);

  const handleAvailabilityChange = (day, field, value) => {
    setAvailability(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        [field]: value
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      if (!currentUser) {
        throw new Error('Not authenticated. Please log in again.');
      }

      if (!userDetails) {
        throw new Error('User details not loaded. Please refresh the page.');
      }

      // Log the state before submission
      console.log('Submitting with:', {
        currentUser: currentUser.uid,
        userDetails,
        availability
      });

      // Prepare the availability document
      const availabilityData = {
        userId: currentUser.uid,
        userName: userDetails.name,
        userEmail: userDetails.email,
        communityId: userDetails.communityId,
        communityCode: userDetails.communityCode,
        availability: availability,
        updatedAt: new Date().toISOString()
      };

      // Save to Firestore
      const availabilityRef = doc(db, 'availability', currentUser.uid);
      await setDoc(availabilityRef, availabilityData, { merge: true });

      setSuccess('Availability updated successfully!');
    } catch (error) {
      console.error('Detailed error:', error);
      setError(error.message || 'Failed to update availability. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center min-vh-100">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  if (!currentUser || !userDetails) {
    return (
      <Container className="py-4">
        <Alert variant="danger">
          You must be logged in to view this page.
          <Button
            variant="link"
            onClick={() => navigate('/login')}
            className="p-0 ms-2"
          >
            Go to Login
          </Button>
        </Alert>
      </Container>
    );
  }

  const days = [
    'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'
  ];

  return (
    <Container className="py-4">
      <Card>
        <Card.Header as="h5" className="bg-primary text-white d-flex justify-content-between align-items-center">
          <span>Weekly Availability</span>
          <span className="text-sm">({userDetails.name})</span>
        </Card.Header>
        <Card.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          {success && <Alert variant="success">{success}</Alert>}

          <Form onSubmit={handleSubmit}>
            {days.map((day) => (
              <Card key={day} className="mb-3">
                <Card.Body>
                  <Row className="align-items-center">
                    <Col md={3}>
                      <Form.Check
                        type="switch"
                        id={`${day}-switch`}
                        label={day.charAt(0).toUpperCase() + day.slice(1)}
                        checked={availability[day].isAvailable}
                        onChange={(e) => handleAvailabilityChange(day, 'isAvailable', e.target.checked)}
                      />
                    </Col>
                    <Col md={9}>
                      <Row className={!availability[day].isAvailable ? 'd-none' : ''}>
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>Start Time</Form.Label>
                            <Form.Control
                              type="time"
                              value={availability[day].startTime}
                              onChange={(e) => handleAvailabilityChange(day, 'startTime', e.target.value)}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>End Time</Form.Label>
                            <Form.Control
                              type="time"
                              value={availability[day].endTime}
                              onChange={(e) => handleAvailabilityChange(day, 'endTime', e.target.value)}
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            ))}

            <div className="d-grid gap-2">
              <Button 
                type="submit" 
                variant="primary" 
                disabled={submitting}
                size="lg"
              >
                {submitting ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Updating...
                  </>
                ) : (
                  'Update Availability'
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>

      {/* Instructions card remains the same */}
    </Container>
  );
};

export default Availability;